/**
 * **C11 · The sponsor invoice, the sponsor receipt and the donation receipt** (Stream C,
 * Documents.dc.html): three half-letter papers. The invoice and the receipt are Money's
 * receivable for a sponsorship (`GET /money/receivables/:id` — its number, what it is for, the
 * amount, received to date, the balance, the due date) with the sponsorship's level and pledge
 * day (Sponsors); the donation receipt is the gift as Commerce holds it with the year's tax line
 * (Year settings). Receipts are signed by the finance director named in Roles, never by CIDER.
 */
import { dollars } from './dollars.js';
import { letterDay } from './payee_letters.js';
import { NO_DATA, type PaperPage } from './paper_page.js';

/** A sponsorship receivable as the papers read it. */
export interface SponsorBill {
  number: string;
  festivalYear: number;
  organizationName: string;
  what: string;
  amountCents: number;
  paidCents: number;
  owedCents: number;
  /** As printed (`Jun 1`). */
  due: string;
  /** The last payment, as printed (`May 12, 2027 · check 2201`); null before any. */
  received: string | null;
  /** The receivable's own Square page for the balance (M21); null prints no QR and no sentence. */
  payPage?: string | null;
}

export interface SponsorPaperInput {
  bill: SponsorBill | null;
  /** The sponsorship's level (`Gold`); null when Sponsors did not say. */
  level: string | null;
  /** The pledge day, as printed (`Mar 3, 2027`). */
  pledged: string | null;
  financeDirector: string | null;
}

const signed = (name: string | null): string[] => [`${name ?? ''} · finance director`, ''];
const level = (l: string | null): string => l ?? NO_DATA;

export function sponsorInvoice(i: SponsorPaperInput): PaperPage {
  const b = i.bill;
  return {
    title: `Invoice · sponsorship ${b ? String(b.festivalYear) : NO_DATA}`,
    sub: b
      ? `${b.organizationName} · ${level(i.level)} · pledged ${i.pledged ?? NO_DATA} · invoice ${b.number} · due ${b.due}`
      : NO_DATA,
    blocks: [
      {
        head: '',
        cols: '1fr 140px',
        rows: b
          ? [
              [b.what, b.amountCents],
              ['Received to date', b.paidCents],
              ['BALANCE', b.owedCents],
            ]
          : [[NO_DATA, '']],
      },
      {
        head: '',
        cols: '1fr',
        rows: [
          [
            b?.payPage
              ? 'Pay by check to Saint Lawrence Apple Fest, or online at the link in the QR.'
              : 'Pay by check to Saint Lawrence Apple Fest.',
          ],
        ],
      },
    ],
    ...(b?.payPage ? { qr: b.payPage } : {}),
    foot: `Sponsor invoice · ${b?.number ?? NO_DATA}`,
  };
}

export function sponsorReceipt(i: SponsorPaperInput): PaperPage {
  const b = i.bill;
  return {
    title: 'Receipt · sponsorship',
    sub: b
      ? `${b.organizationName} · ${level(i.level)} · received ${b.received ?? NO_DATA}`
      : NO_DATA,
    blocks: [
      {
        head: '',
        cols: '1fr 140px',
        rows: b
          ? [
              [`${level(i.level)} sponsorship ${String(b.festivalYear)}`, b.paidCents],
              // What a sponsor's benefits are worth is recorded nowhere yet (WORKLIST K11).
              ['Fair value of what you receive', NO_DATA],
              ['Deductible portion', NO_DATA],
            ]
          : [[NO_DATA, '']],
      },
    ],
    sign: signed(i.financeDirector),
    foot: 'Sponsor receipt',
  };
}

export interface DonationReceiptInput {
  gift: { name: string; cents: number } | null;
  /** The gift's day (YYYY-MM-DD); null when it is not recorded (WORKLIST K12). */
  on: string | null;
  /** The year's tax line (Year settings); null when nobody has typed it. */
  taxLine: string | null;
  financeDirector: string | null;
}

const LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
/** `2026-09-14` → `September 14, 2026`. */
const longDay = (ymd: string | null): string => {
  const m = ymd ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd) : null;
  return m ? `${LONG[Number(m[2]) - 1] ?? ''} ${String(Number(m[3]))}, ${m[1] ?? ''}` : NO_DATA;
};

export function donationReceipt(i: DonationReceiptInput): PaperPage {
  const g = i.gift;
  return {
    title: 'Receipt · donation',
    sub: g ? `${g.name} · ${dollars(g.cents)} · ${i.on ? letterDay(i.on) : NO_DATA}` : NO_DATA,
    blocks: [
      {
        head: '',
        cols: '1fr',
        rows: [
          [
            g
              ? `Thank you for your gift of ${dollars(g.cents)} to Saint Lawrence Apple Fest on ${longDay(i.on)}. ${i.taxLine ?? `The tax line: ${NO_DATA} — Year settings.`}`
              : `${NO_DATA} — the gift is not in Commerce.`,
          ],
        ],
      },
    ],
    sign: signed(i.financeDirector),
    foot: 'Donation receipt',
  };
}
